/**
 * TanStack Query hooks for the sales domain.
 *
 * Read hooks consume the list / single endpoints from spec sections
 * 6.4 (leads) and 6.5 (opportunities). Mutation hooks wrap the
 * state-machine actions with cache invalidation.
 *
 * Reference: spec sections 6.4, 6.5, 7.3.
 */

import { useAuth } from '@clerk/clerk-react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateLeadInput,
  ConvertLeadInput,
  LogLeadActionInput,
  UpdateLeadInput,
  CreateOpportunityInput,
  UpdateOpportunityInput,
  AdvanceOpportunityStepInput,
  Subtask,
  SubtaskCreateInput,
  SubtaskUpdateInput,
  AuditEntry,
} from '@workspace/shared';

import { apiRequest } from '../api-client';

export const LEADS_KEY = ['sales', 'leads'] as const;
export const OPPS_KEY = ['sales', 'opportunities'] as const;

interface WireLead {
  id: string;
  companyName: string;
  contactName: string | null;
  contactRole: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  source: string;
  stage: string;
  snoozeUntil: string | null;
  auditLog?: AuditEntry[];
  /** Step 1: canonical user-set action; the client derives Next Action +
   * stage from it. `nextAction` (legacy) stays for the transitional release. */
  currentAction: string;
  nextAction: string;
  nextActionDue: string | null;
  lastAction: string | null;
  lastActionAt: string | null;
  oppType: string | null;
  capability: string | null;
  customerType: string | null;
  /** Step 1: enum industry; `industryVertical` retained transitionally. */
  industry: string | null;
  industryVertical: string | null;
  currentCustomer: boolean;
  potentialValue: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  convertedAt: string | null;
  disqualifiedAt: string | null;
  convertedOpportunityId: string | null;
  /** Optional child subtasks. UAT-28 surfaces these on both the list
   * and single-lead endpoints so the slide-over checklist paints on
   * first load. List payloads include the relation; mutation responses
   * (POST/PATCH/log-action/etc) omit it because the slide-over reads
   * the dedicated single endpoint anyway. */
  subtasks?: Subtask[];
}

interface WireOpp {
  id: string;
  companyName: string;
  opportunityName: string;
  /** Step 1: contact carried from the lead. */
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  oppType: string;
  capability: string | null;
  stage: string;
  /** Step 1: canonical action + derived next action; `currentStep` legacy. */
  currentAction: string;
  nextAction: string | null;
  currentStep: string;
  winProbability: string;
  snoozeUntil: string | null;
  auditLog?: AuditEntry[];
  budgetFee: string | null;
  budgetSignal: string;
  customerType: string | null;
  customerStatus: string;
  source: string;
  nextActionDue: string | null;
  lastActionAt: string | null;
  proposalDocUrl: string | null;
  notes: string | null;
  sourceLeadId: string | null;
  createdAt: string;
  updatedAt: string;
  wonAt: string | null;
  lostAt: string | null;
  disqualifiedAt: string | null;
  /** Optional child subtasks. UAT-28 surfaces these on the opportunity
   * list and single endpoints; mutation responses still return the
   * bare row because the slide-over re-fetches the single endpoint. */
  subtasks?: Subtask[];
}

interface WireQueueItem {
  id: string;
  kind: 'lead' | 'opportunity';
  // Tabs-updates: the queue is staleness-based now (single reason).
  reason: 'STALE';
  companyName: string;
  contactName: string | null;
  opportunityName: string | null;
  suggestedAction: string;
  /** Days since the last action (or creation). */
  ageDays: number;
  stage: string;
  currentAction: string;
  importanceScore: number;
}

interface WirePipelineGlance {
  openCount: number;
  totalValue: number;
  weightedValue: number;
  stalledCount: number;
  byStage: Record<string, number>;
}

interface WireFroggedTask {
  id: string;
  title: string;
  status: string;
  type: string;
  priority: string;
}

interface WireRecentWin {
  id: string;
  companyName: string;
  opportunityName: string;
  budgetFee: string | null;
  wonAt: string | null;
}

interface WireMorningBrief {
  greeting: string;
  todayIso: string;
  queue: WireQueueItem[];
  committed: WireQueueItem[];
  dailyCommitTarget: number;
  threePmBanner: boolean;
  zeroDayFlag: boolean;
  yesterdayActionCount: number;
  pipelineGlance: WirePipelineGlance;
  froggedTasks: WireFroggedTask[];
  recentWins: WireRecentWin[];
}

export const QUEUE_KEY = ['sales', 'queue'] as const;
export const MORNING_BRIEF_KEY = ['sales', 'morning-brief'] as const;

export type {
  WireLead,
  WireOpp,
  WireQueueItem,
  WireMorningBrief,
  WirePipelineGlance,
  WireFroggedTask,
  WireRecentWin,
};

function useTokenGetter() {
  const { getToken } = useAuth();
  return async (): Promise<string> => {
    const token = await getToken();
    if (!token) throw new Error('No active Clerk session');
    return token;
  };
}

// ─── Leads ─────────────────────────────────────────────────────────

export function useLeads(): UseQueryResult<{ leads: WireLead[]; total: number }, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: [...LEADS_KEY, 'list'],
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<{ leads: WireLead[]; total: number }>('/api/v1/leads', { token });
    },
  });
}

export function useLead(id: string | null): UseQueryResult<WireLead, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: [...LEADS_KEY, 'single', id],
    enabled: id !== null,
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<WireLead>(`/api/v1/leads/${id}`, { token });
    },
  });
}

export function useCreateLead(): UseMutationResult<WireLead, Error, CreateLeadInput> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const token = await tokenGetter();
      return apiRequest<WireLead>('/api/v1/leads', { method: 'POST', body: input, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

export function useUpdateLead(): UseMutationResult<
  WireLead,
  Error,
  { id: string; patch: UpdateLeadInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const token = await tokenGetter();
      return apiRequest<WireLead>(`/api/v1/leads/${id}`, { method: 'PATCH', body: patch, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

export function useLogLeadAction(): UseMutationResult<
  { lead: WireLead; taskSpawnSuggestion: { reason: string; suggestedTitle: string } | null },
  Error,
  { id: string; body: LogLeadActionInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const token = await tokenGetter();
      return apiRequest(`/api/v1/leads/${id}/log-action`, { method: 'POST', body, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

export function useConvertLead(): UseMutationResult<
  { lead: WireLead; opportunity: { id: string; opportunityName: string; companyName: string } },
  Error,
  { id: string; body: ConvertLeadInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const token = await tokenGetter();
      return apiRequest(`/api/v1/leads/${id}/convert`, { method: 'POST', body, token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

export function useDisqualifyLead(): UseMutationResult<
  WireLead,
  Error,
  { id: string; reason?: string }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const token = await tokenGetter();
      return apiRequest<WireLead>(`/api/v1/leads/${id}/disqualify`, {
        method: 'POST',
        body: { reason },
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

export function useRecycleLead(): UseMutationResult<WireLead, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<WireLead>(`/api/v1/leads/${id}/recycle`, { method: 'POST', token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

/**
 * Soft-delete a lead via the backend's DELETE /leads/:id endpoint.
 *
 * UAT-39 surfaced the gap: Peter had no way to remove a lead from the
 * UI even though the backend already accepted DELETE (sets `stage` to
 * DEAD, stamps `disqualifiedAt`, writes a DISQUALIFIED audit row, and
 * returns `{ success: true }`). This hook drives the new footer
 * button on `LeadSlideOver`; invalidating LEADS_KEY refreshes the
 * board so the row drops out of TARGET / ACTIVE / CONVERTED and
 * appears in the DEAD column.
 */
export function useDeleteLead(): UseMutationResult<{ success: true }, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<{ success: true }>(`/api/v1/leads/${id}`, {
        method: 'DELETE',
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

// ─── Opportunities ─────────────────────────────────────────────────

export function useOpportunities(): UseQueryResult<
  { opportunities: WireOpp[]; total: number },
  Error
> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: [...OPPS_KEY, 'list'],
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<{ opportunities: WireOpp[]; total: number }>('/api/v1/opportunities', {
        token,
      });
    },
  });
}

export function useOpportunity(id: string | null): UseQueryResult<WireOpp, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: [...OPPS_KEY, 'single', id],
    enabled: id !== null,
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<WireOpp>(`/api/v1/opportunities/${id}`, { token });
    },
  });
}

export function useCreateOpportunity(): UseMutationResult<WireOpp, Error, CreateOpportunityInput> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const token = await tokenGetter();
      return apiRequest<WireOpp>('/api/v1/opportunities', {
        method: 'POST',
        body: input,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

export function useUpdateOpportunity(): UseMutationResult<
  WireOpp,
  Error,
  { id: string; patch: UpdateOpportunityInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }) => {
      const token = await tokenGetter();
      return apiRequest<WireOpp>(`/api/v1/opportunities/${id}`, {
        method: 'PATCH',
        body: patch,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

export function useAdvanceOpportunityStep(): UseMutationResult<
  WireOpp,
  Error,
  { id: string; body: AdvanceOpportunityStepInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const token = await tokenGetter();
      return apiRequest<WireOpp>(`/api/v1/opportunities/${id}/advance-step`, {
        method: 'POST',
        body,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

export function useWinOpportunity(): UseMutationResult<WireOpp, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<WireOpp>(`/api/v1/opportunities/${id}/win`, { method: 'POST', token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

export function useLoseOpportunity(): UseMutationResult<WireOpp, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<WireOpp>(`/api/v1/opportunities/${id}/lose`, { method: 'POST', token });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

/**
 * Soft-delete an opportunity via DELETE /opportunities/:id.
 *
 * UAT-39 mirror of {@link useDeleteLead}: the backend already accepts
 * DELETE (stamps `disqualifiedAt`, writes a DISQUALIFIED audit row,
 * returns `{ success: true }`) but the UI never exposed it. This hook
 * drives the new footer button on `OpportunitySlideOver`. Invalidating
 * OPPS_KEY refreshes both the opportunity board and the pipeline board
 * so the row drops out of view.
 */
export function useDeleteOpportunity(): UseMutationResult<{ success: true }, Error, string> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const token = await tokenGetter();
      return apiRequest<{ success: true }>(`/api/v1/opportunities/${id}`, {
        method: 'DELETE',
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

// ─── Daily queue + morning brief (M9) ──────────────────────────────

export function useSalesQueue(): UseQueryResult<
  { items: WireQueueItem[]; committed: WireQueueItem[] },
  Error
> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: QUEUE_KEY,
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<{ items: WireQueueItem[]; committed: WireQueueItem[] }>(
        '/api/v1/sales/queue',
        { token },
      );
    },
    // 60s refetch keeps the queue fresh as background tabs log actions.
    refetchInterval: 60_000,
  });
}

export function useCommitQueue(): UseMutationResult<
  { committed: WireQueueItem[] },
  Error,
  string[]
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (itemIds) => {
      const token = await tokenGetter();
      return apiRequest<{ committed: WireQueueItem[] }>('/api/v1/sales/queue/commit', {
        method: 'POST',
        body: { itemIds },
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QUEUE_KEY });
      void qc.invalidateQueries({ queryKey: MORNING_BRIEF_KEY });
    },
  });
}

export function useMorningBrief(): UseQueryResult<WireMorningBrief, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: MORNING_BRIEF_KEY,
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<WireMorningBrief>('/api/v1/morning-brief', { token });
    },
  });
}

/** Sales summary aggregates for the Summary dashboard (Tabs-updates). */
export interface WireSalesSummary {
  leads: {
    total: number;
    byStage: Record<string, number>;
    byCurrentAction: Record<string, number>;
    bySource: Record<string, number>;
    byIndustry: Record<string, number>;
    totalPotentialValue: number;
  };
  opportunities: {
    total: number;
    openCount: number;
    byStage: Record<string, number>;
    byCurrentAction: Record<string, number>;
    byCustomerStatus: Record<string, number>;
    totalValue: number;
    weightedValue: number;
  };
}

const SUMMARY_KEY = ['sales', 'summary'] as const;

export function useSalesSummary(): UseQueryResult<WireSalesSummary, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: async () => {
      const token = await tokenGetter();
      return apiRequest<WireSalesSummary>('/api/v1/sales/summary', { token });
    },
  });
}

// ────────────────────────────────────────────────────────────────────
// Lead subtasks (UAT-28)
// ────────────────────────────────────────────────────────────────────

/**
 * Create a new subtask under the given lead. The backend assigns the
 * next orderIndex and returns the created row. Invalidates LEADS_KEY
 * so the slide-over re-renders with the new entry appended.
 */
export function useCreateLeadSubtask(): UseMutationResult<
  Subtask,
  Error,
  { leadId: string; input: SubtaskCreateInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, input }) => {
      const token = await tokenGetter();
      return apiRequest<Subtask>(`/api/v1/leads/${leadId}/subtasks`, {
        method: 'POST',
        body: input,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

/**
 * Patch a lead subtask (title, done, orderIndex). `done=true` triggers
 * the backend's completedAt stamp.
 */
export function useUpdateLeadSubtask(): UseMutationResult<
  Subtask,
  Error,
  { leadId: string; subtaskId: string; patch: SubtaskUpdateInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, subtaskId, patch }) => {
      const token = await tokenGetter();
      return apiRequest<Subtask>(`/api/v1/leads/${leadId}/subtasks/${subtaskId}`, {
        method: 'PATCH',
        body: patch,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

/** Hard-delete a lead subtask. */
export function useDeleteLeadSubtask(): UseMutationResult<
  { success: true },
  Error,
  { leadId: string; subtaskId: string }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ leadId, subtaskId }) => {
      const token = await tokenGetter();
      return apiRequest<{ success: true }>(`/api/v1/leads/${leadId}/subtasks/${subtaskId}`, {
        method: 'DELETE',
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LEADS_KEY });
    },
  });
}

// ────────────────────────────────────────────────────────────────────
// Opportunity subtasks (UAT-28)
// ────────────────────────────────────────────────────────────────────

/**
 * Create a new subtask under the given opportunity. Mirror of the lead
 * variant; backend assigns the next orderIndex. Invalidates OPPS_KEY
 * so the slide-over re-renders.
 */
export function useCreateOpportunitySubtask(): UseMutationResult<
  Subtask,
  Error,
  { opportunityId: string; input: SubtaskCreateInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ opportunityId, input }) => {
      const token = await tokenGetter();
      return apiRequest<Subtask>(`/api/v1/opportunities/${opportunityId}/subtasks`, {
        method: 'POST',
        body: input,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

/**
 * Patch an opportunity subtask (title, done, orderIndex). Mirrors
 * the lead variant.
 */
export function useUpdateOpportunitySubtask(): UseMutationResult<
  Subtask,
  Error,
  { opportunityId: string; subtaskId: string; patch: SubtaskUpdateInput }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ opportunityId, subtaskId, patch }) => {
      const token = await tokenGetter();
      return apiRequest<Subtask>(`/api/v1/opportunities/${opportunityId}/subtasks/${subtaskId}`, {
        method: 'PATCH',
        body: patch,
        token,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

/** Hard-delete an opportunity subtask. */
export function useDeleteOpportunitySubtask(): UseMutationResult<
  { success: true },
  Error,
  { opportunityId: string; subtaskId: string }
> {
  const tokenGetter = useTokenGetter();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ opportunityId, subtaskId }) => {
      const token = await tokenGetter();
      return apiRequest<{ success: true }>(
        `/api/v1/opportunities/${opportunityId}/subtasks/${subtaskId}`,
        { method: 'DELETE', token },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: OPPS_KEY });
    },
  });
}

/** A single stats data point (Tabs-updates Stats tab). */
export interface StatsPoint {
  label: string;
  value: number;
}

/** Stats endpoint response. */
export interface WireStats {
  metric: string;
  from: string;
  to: string;
  kind: 'count' | 'currency';
  points: StatsPoint[];
}

/**
 * Fetch a stats metric over a date window. The query key includes the
 * params so switching metric / range refetches.
 */
export function useSalesStats(
  metric: string,
  fromIso: string,
  toIso: string,
): UseQueryResult<WireStats, Error> {
  const tokenGetter = useTokenGetter();
  return useQuery({
    queryKey: ['sales', 'stats', metric, fromIso, toIso],
    queryFn: async () => {
      const token = await tokenGetter();
      const params = new URLSearchParams({ metric, from: fromIso, to: toIso });
      return apiRequest<WireStats>(`/api/v1/sales/stats?${params.toString()}`, { token });
    },
  });
}
